import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { accountsAPI, type AccountOption } from "../../services/api";

export interface ScheduledReport {
	_id: string;
	email: string;
	lastSentAt?: string | null;
}

export interface Account {
	_id: string;
	name: string;
	description: string;
	contactEmail: string;
	maxSeats: number;
	isActive: boolean;
	tags: string[];
	createdAt: string;
	scheduledReports?: ScheduledReport[];
}

interface AccountsResponse {
	accounts: Account[];
	total: number;
}

export interface AccountStats {
	total: number;
	active: number;
}

interface AccountsState {
	accounts: Account[];
	// Every account's name, independent of the Accounts page's paged, searched
	// list - sharing that list let visiting Accounts wipe the Licenses filter
	options: AccountOption[];
	hasLoadedOptions: boolean;
	loading: boolean;
	error: string | null;
	total: number;
	page: number;
	limit: number;
	// Id of the most recent list request, so a slower earlier one cannot
	// overwrite it: a delete refreshes the current page while the page number is
	// being corrected, leaving two fetches in flight for different pages.
	latestFetchRequestId: string | null;
	// Counted over every account, not the page of them on screen
	stats: AccountStats;
	// The same counts under the page's filters, null while unfiltered
	filteredStats: AccountStats | null;
	latestFilteredStatsRequestId: string | null;
}

const initialState: AccountsState = {
	accounts: [],
	options: [],
	hasLoadedOptions: false,
	loading: false,
	error: null,
	total: 0,
	page: 1,
	limit: 10,
	latestFetchRequestId: null,
	stats: { total: 0, active: 0 },
	filteredStats: null,
	latestFilteredStatsRequestId: null,
};

// Async thunks
export const fetchAccountOptions = createAsyncThunk(
	"accounts/fetchAccountOptions",
	async (_, { rejectWithValue }) => {
		try {
			const response = await accountsAPI.getOptions();
			return response.data.data.accounts;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to load accounts"
				);
			}
			return rejectWithValue("Failed to load accounts");
		}
	}
);

export const fetchAccounts = createAsyncThunk(
	"accounts/fetchAccounts",
	async (
		params: {
			page?: number;
			limit?: number;
			search?: string;
			isActive?: boolean;
			sortBy?: string;
			sortOrder?: string;
		} = {},
		{ rejectWithValue }
	) => {
		try {
			const response = await accountsAPI.getAll(params);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch accounts"
				);
			}
			return rejectWithValue("Failed to fetch accounts");
		}
	}
);

export const fetchFilteredAccountStats = createAsyncThunk(
	"accounts/fetchFilteredAccountStats",
	async (filters: { search?: string }, { rejectWithValue }) => {
		try {
			const response = await accountsAPI.getStats(filters);
			return response.data.data as AccountStats;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch filtered stats"
				);
			}
			return rejectWithValue("Failed to fetch filtered stats");
		}
	}
);

export const fetchAccountStats = createAsyncThunk(
	"accounts/fetchAccountStats",
	async (_params: void, { rejectWithValue }) => {
		try {
			const response = await accountsAPI.getStats();
			return response.data.data as AccountStats;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch account stats"
				);
			}
			return rejectWithValue("Failed to fetch account stats");
		}
	}
);

export const createAccount = createAsyncThunk(
	"accounts/createAccount",
	async (
		accountData: {
			name: string;
			description?: string;
			contactEmail: string;
			maxSeats?: number;
			tags?: string[];
			reportEmails?: string[];
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await accountsAPI.create(accountData);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to create account"
				);
			}
			return rejectWithValue("Failed to create account");
		}
	}
);

export const updateAccount = createAsyncThunk(
	"accounts/updateAccount",
	async (
		{
			id,
			...accountData
		}: {
			id: string;
			name: string;
			description?: string;
			contactEmail: string;
			maxSeats?: number;
			tags?: string[];
			reportEmails?: string[];
			isActive: boolean;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await accountsAPI.update(id, accountData);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to update account"
				);
			}
			return rejectWithValue("Failed to update account");
		}
	}
);

export const deleteAccount = createAsyncThunk(
	"accounts/deleteAccount",
	async (id: string, { rejectWithValue }) => {
		try {
			await accountsAPI.delete(id);
			return id;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to delete account"
				);
			}
			return rejectWithValue("Failed to delete account");
		}
	}
);

export const exportAccountLicenses = createAsyncThunk(
	"accounts/exportAccountLicenses",
	async (
		{ accountId, accountName }: { accountId: string; accountName: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await accountsAPI.exportLicenses(accountId);

			// Create a blob URL and trigger download
			const blob = new Blob([response.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${accountName.replace(/[^a-zA-Z0-9]/g, "_")}_licenses_${
				new Date().toISOString().split("T")[0]
			}.xlsx`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			return { accountId, success: true };
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { message?: string } };
				};
				return rejectWithValue(
					axiosError.response?.data?.message ||
						"Failed to export account licenses"
				);
			}
			return rejectWithValue("Failed to export account licenses");
		}
	}
);

export const exportAccountStatusReport = createAsyncThunk(
	"accounts/exportAccountStatusReport",
	async (
		{ accountId, accountName }: { accountId: string; accountName: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await accountsAPI.exportStatusReport(accountId);

			// Create a blob URL and trigger download
			const blob = new Blob([response.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${accountName.replace(
				/[^a-zA-Z0-9]/g,
				"_"
			)}_license_status_${new Date().toISOString().split("T")[0]}.xlsx`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			return { accountId, success: true };
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { message?: string } };
				};
				return rejectWithValue(
					axiosError.response?.data?.message ||
						"Failed to export license status report"
				);
			}
			return rejectWithValue("Failed to export license status report");
		}
	}
);

export const sendAccountReport = createAsyncThunk(
	"accounts/sendAccountReport",
	async (
		{
			accountId,
			reportType,
			emails,
			toSubscribers,
			scheduleMonthly,
			sendNow,
		}: {
			accountId: string;
			reportType: "billing" | "status";
			emails?: string[];
			toSubscribers?: boolean;
			scheduleMonthly?: boolean;
			sendNow?: boolean;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await accountsAPI.sendReport(accountId, {
				reportType,
				emails,
				toSubscribers,
				scheduleMonthly,
				sendNow,
			});
			const { sent, failed, subscribers } = response.data.data;
			return {
				accountId,
				sent,
				failed,
				subscribers,
				message: response.data.message,
			};
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { message?: string } };
				};
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to send report"
				);
			}
			return rejectWithValue("Failed to send report");
		}
	}
);

export const removeScheduledReport = createAsyncThunk(
	"accounts/removeScheduledReport",
	async (
		{ accountId, email }: { accountId: string; email: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await accountsAPI.removeScheduledReport(
				accountId,
				email
			);
			return { accountId, email, message: response.data.message };
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { message?: string } };
				};
				return rejectWithValue(
					axiosError.response?.data?.message ||
						"Failed to remove scheduled report"
				);
			}
			return rejectWithValue("Failed to remove scheduled report");
		}
	}
);

const accountsSlice = createSlice({
	name: "accounts",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearFilteredStats: (state) => {
			state.filteredStats = null;
			state.latestFilteredStatsRequestId = null;
		},
		setPage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		setLimit: (state, action: PayloadAction<number>) => {
			state.limit = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAccountOptions.fulfilled, (state, action) => {
				state.options = action.payload;
				state.hasLoadedOptions = true;
			})
			.addCase(fetchAccountOptions.rejected, (state, action) => {
				state.error = action.payload as string;
			})
			// Fetch accounts
			.addCase(fetchAccounts.pending, (state, action) => {
				state.loading = true;
				state.error = null;
				state.latestFetchRequestId = action.meta.requestId;
			})
			.addCase(fetchAccounts.fulfilled, (state, action) => {
				if (state.latestFetchRequestId !== action.meta.requestId) return;
				state.loading = false;
				const data = action.payload.data as AccountsResponse;
				state.accounts = data?.accounts || [];
				state.total = data?.total || 0;
			})
			.addCase(fetchAccounts.rejected, (state, action) => {
				if (state.latestFetchRequestId !== action.meta.requestId) return;
				state.loading = false;
				state.error = action.payload as string;
			})
			// Filters change as fast as the user types; an older response must
			// not replace the counts for what is on screen now
			.addCase(fetchFilteredAccountStats.pending, (state, action) => {
				state.latestFilteredStatsRequestId = action.meta.requestId;
			})
			.addCase(fetchFilteredAccountStats.fulfilled, (state, action) => {
				if (state.latestFilteredStatsRequestId !== action.meta.requestId) return;
				state.filteredStats = action.payload;
			})
			.addCase(fetchFilteredAccountStats.rejected, (state, action) => {
				if (state.latestFilteredStatsRequestId !== action.meta.requestId) return;
				state.filteredStats = null;
				state.error = action.payload as string;
			})
			.addCase(fetchAccountStats.fulfilled, (state, action) => {
				state.stats = action.payload;
			})
			.addCase(fetchAccountStats.rejected, (state, action) => {
				state.error = action.payload as string;
			})
			// Create account
			.addCase(createAccount.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createAccount.fulfilled, (state, action) => {
				state.loading = false;
				const data = action.payload.data as { account: Account };
				if (data) {
					state.accounts.unshift(data.account);
					state.total += 1;
				}
			})
			.addCase(createAccount.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Update account
			.addCase(updateAccount.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateAccount.fulfilled, (state, action) => {
				state.loading = false;
				const data = action.payload.data as { account: Account };
				if (data) {
					const index = state.accounts.findIndex(
						(account) => account._id === data.account._id
					);
					if (index !== -1) {
						state.accounts[index] = data.account;
					}
				}
			})
			.addCase(updateAccount.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Delete account
			.addCase(deleteAccount.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteAccount.fulfilled, (state, action) => {
				state.loading = false;
				state.accounts = state.accounts.filter(
					(account) => account._id !== action.payload
				);
				state.total -= 1;
			})
			.addCase(deleteAccount.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Export account licenses
			.addCase(exportAccountLicenses.pending, (state) => {
				// Don't set loading state - this doesn't need to reload the page
				state.error = null;
			})
			.addCase(exportAccountLicenses.fulfilled, () => {
				// No loading state to reset
			})
			.addCase(exportAccountLicenses.rejected, (state, action) => {
				state.error = action.payload as string;
			})
			// Export account status report
			.addCase(exportAccountStatusReport.pending, (state) => {
				// Don't set loading state - this doesn't need to reload the page
				state.error = null;
			})
			.addCase(exportAccountStatusReport.fulfilled, () => {
				// No loading state to reset
			})
			.addCase(exportAccountStatusReport.rejected, (state, action) => {
				state.error = action.payload as string;
			})
			// Send account report
			.addCase(sendAccountReport.pending, (state) => {
				// Don't set loading state - this is handled locally in the component
				state.error = null;
			})
			.addCase(sendAccountReport.fulfilled, (state, action) => {
				const account = state.accounts.find(
					(acc) => acc._id === action.payload.accountId
				);
				if (account) {
					account.scheduledReports = action.payload.subscribers;
				}
			})
			.addCase(sendAccountReport.rejected, (state, action) => {
				state.error = action.payload as string;
			})
			// Remove scheduled report
			.addCase(removeScheduledReport.pending, (state) => {
				state.error = null;
			})
			.addCase(removeScheduledReport.fulfilled, (state, action) => {
				// Remove the scheduled report from the account
				const account = state.accounts.find(
					(acc) => acc._id === action.payload.accountId
				);
				if (account && account.scheduledReports) {
					account.scheduledReports = account.scheduledReports.filter(
						(report) => report.email !== action.payload.email
					);
				}
			})
			.addCase(removeScheduledReport.rejected, (state, action) => {
				state.error = action.payload as string;
			});
	},
});

export const { clearError, clearFilteredStats, setPage, setLimit } = accountsSlice.actions;
export default accountsSlice.reducer;
